<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('super-mobile-features')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Edit Feature</h1>
        </header>
        <section class="module__body">
            <form
                class="full-width"
                @submit.prevent="updateFeature"
            >
                <div class="form-group">
                    <label for="name">Name</label>
                    <input
                        id="name"
                        v-model="name"
                        type="text"
                        data-test-id="feature-name"
                        required
                        maxlength="120"
                        @keyup="nameChanged"
                    />
                    <p
                        class="help"
                        data-test-id="feature-value"
                    >
                        {{ feature }}
                    </p>
                </div>
                <div class="form-group">
                    <label for="description">Description</label>
                    <textarea
                        id="description"
                        v-model="description"
                        data-test-id="feature-description"
                        class="short"
                        required
                        maxlength="191"
                    />
                </div>
                <div class="form-group">
                    <slider-toggle-field
                        name="useCustomSetting"
                        label="Use Custom Setting"
                        description="Customizable setting field for mobile clients"
                        data-test-id="feature-custom-setting-toggle"
                        :value="Boolean(custom_setting_enabled)"
                        @update="custom_setting_enabled = $event"
                    ></slider-toggle-field>
                </div>
                <div
                    v-if="custom_setting_enabled"
                    class="form-group"
                >
                    <label for="custom_setting_name">Custom Setting Name</label>
                    <input
                        id="custom_setting_name"
                        v-model="custom_setting_name"
                        data-test-id="feature-custom-setting-name"
                        type="text"
                        required
                        maxlength="32"
                    />
                </div>
                <div
                    v-if="custom_setting_enabled"
                    class="form-group"
                >
                    <label for="custom_setting_value">Custom Setting Value</label>
                    <input
                        id="custom_setting_value"
                        v-model="custom_setting_value"
                        data-test-id="feature-custom-setting-value"
                        type="text"
                        required
                        maxlength="120"
                    />
                </div>
                <div
                    v-if="custom_setting_enabled"
                    class="form-group"
                >
                    <label for="custom_setting_description">Custom Setting Description</label>
                    <textarea
                        id="custom_setting_description"
                        v-model="custom_setting_description"
                        data-test-id="feature-custom-setting-description"
                        class="short"
                        required
                    />
                </div>
                <div class="form-group">
                    <slider-toggle-field
                        name="isPremiumFeature"
                        label="Premium Feature"
                        data-test-id="feature-premium-toggle"
                        :value="is_premium"
                        @update="togglePremium"
                    ></slider-toggle-field>
                </div>
                <div class="form-group">
                    <slider-toggle-field
                        v-if="is_premium"
                        label="Upsell Message Enabled"
                        data-test-id="feature-upsell-message-toggle"
                        :value="upsell_message_enabled"
                        @update="upsell_message_enabled = $event"
                    ></slider-toggle-field>
                </div>
                <div
                    v-if="is_premium && upsell_message_enabled"
                    class="form-group"
                >
                    <label for="upsell_message">Upsell Message</label>
                    <textarea
                        id="upsell_message"
                        v-model="upsell_message"
                        name="upsell_message"
                        data-test-id="feature-upsell-message-value"
                        class="short upsell-text"
                        maxlength="191"
                        placeholder="Upsell message copy"
                        required
                    />
                </div>

                <div class="form-group multi-select">
                    <multi-select-field
                        :allItems="allPermissions"
                        label="API Permissions"
                        testId="permissions-select"
                        :selectedValues="selectedPermissionIdsArray"
                        @items-selected="handlePermissionsSelected"
                        @items-unselected="handlePermissionsUnselected"
                    ></multi-select-field>
                </div>

                <form-errors :response="errorResponse"></form-errors>
                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        data-test-id="feature-update-button"
                        :disabled="!formReady"
                        :class="{ active: submitted }"
                    >
                        <div>Update</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import SliderToggleField from '@/components/SliderToggleField.vue'
import MultiSelectField from '@/components/MultiSelectField.vue'

export default {
    components: {
        FormErrors,
        MultiSelectField,
        SliderToggleField,
    },

    mixins: [canToast, hasBackButton],

    data() {
        return {
            id: this.$route.params.id,
            name: null,
            feature: null,
            description: null,
            upsell_message: null,
            upsell_message_enabled: null,
            is_premium: false,
            custom_setting_enabled: false,
            custom_setting_name: null,
            custom_setting_value: null,
            custom_setting_description: null,
            allPermissions: [],
            selectedPermissionIds: new Set(),
            formReady: false,
            errorResponse: null,
            submitted: false,
        }
    },

    computed: {
        selectedPermissionIdsArray() {
            return Array.from(this.selectedPermissionIds)
        },
    },

    mounted() {
        const mobileFeature = this.$http.get(`mobile-features/${this.id}`).then(({ data }) => {
            this.name = data.name
            this.feature = data.feature
            this.description = data.description
            this.upsell_message = data.upsell_message
            this.upsell_message_enabled = Boolean(data.upsell_message_enabled)
            this.is_premium = Boolean(data.is_premium)
            this.custom_setting_enabled = data.custom_setting_enabled
            this.custom_setting_name = data.custom_setting_name
            this.custom_setting_value = data.custom_setting_value
            this.custom_setting_description = data.custom_setting_description

            this.selectedPermissionIds.clear()
            data.permissions.forEach(({ id }) => this.selectedPermissionIds.add(id))
        })

        const permissions = this.$http.get('/permissions/all').then(({ data }) => {
            this.allPermissions = data.map(({ id, name }) => ({
                id,
                name,
            }))
        })

        Promise.all([mobileFeature, permissions]).then(() => {
            this.formReady = true
        })
    },

    methods: {
        updateFeature() {
            this.submitted = true

            this.$http
                .put(`mobile-features/${this.id}`, {
                    name: this.name,
                    feature: this.feature,
                    description: this.description,
                    upsell_message: this.upsell_message,
                    upsell_message_enabled: this.upsell_message_enabled,
                    is_premium: this.is_premium,
                    custom_setting_enabled: this.custom_setting_enabled,
                    custom_setting_name: this.custom_setting_name,
                    custom_setting_value: this.custom_setting_value,
                    custom_setting_description: this.custom_setting_description,
                    permissions: this.selectedPermissionIdsArray,
                })
                .then(() => {
                    this.toast('Mobile Feature successfully updated!')
                    this.$router.push('/mobile-features')
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
        nameChanged(event) {
            const { value } = event.target
            this.feature = value
                .replaceAll(' ', '_')
                .replaceAll(/[^a-zA-Z_ ]/g, '')
                .toUpperCase()
        },
        togglePremium(value) {
            this.is_premium = value
            if (!value) {
                this.upsell_message_enabled = false
            }
        },
        handlePermissionsSelected(ids) {
            ids.forEach((id) => {
                this.selectedPermissionIds.add(id)
            })
        },
        handlePermissionsUnselected(ids) {
            ids.forEach((id) => {
                this.selectedPermissionIds.delete(id)
            })
        },
    },
}
</script>

<style scoped lang="scss">
.upsell-text {
    margin-top: 12px;
}
</style>
